import { ValueGetterParams } from 'ag-grid-community'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../../domain/entity/WbsItemEntity'
import {
  ProgramManagementGridContext,
  ProgramManagementProgress,
  ProgramManagementRow,
} from '../../../programManagement'

type ProductivityMetric = 'sv' | 'spi' | 'cv' | 'cpi' | 'etc' | 'eac'

/** context の aggregateTarget / aggregateField に応じた進捗データを返す */
const getActiveProgress = (
  params: ValueGetterParams<ProgramManagementRow>
): ProgramManagementProgress | null => {
  const progress = params.data?.progress
  if (!progress) return null

  const ctx = params.context as ProgramManagementGridContext | undefined
  const isTask = ctx?.aggregateTarget === WbsItemType.TASK
  const isCount = !ctx || ctx.aggregateField === AggregateField.WBS_ITEM_COUNT

  if (isTask) {
    return isCount ? progress.taskCount : progress.taskWorkload
  }
  return isCount ? progress.deliverableCount : progress.deliverableWorkload
}

/**
 * ネストされたデータから値を取得する。
 * progress カテゴリの場合は context に応じたバリエーションを選択する。
 */
export const getAggregatedValue = (
  params: ValueGetterParams<ProgramManagementRow>,
  category: 'progress' | 'resource' | 'profitLoss',
  field: string
): number | null => {
  const data = params.data
  if (!data) return null

  if (category === 'progress') {
    const progress = getActiveProgress(params)
    if (!progress) return null
    const value = (progress as Record<string, unknown>)[field]
    return typeof value === 'number' ? value : null
  }

  const categoryData = data[category]
  if (!categoryData) return null
  const value = (categoryData as Record<string, unknown>)[field]
  return typeof value === 'number' ? value : null
}

/**
 * 生産性指標の計算には常に工数(workload)ベースの進捗を使用する。
 * actualResource（sumActualHour）が時間単位のため、
 * EV/PV/BAC も同じ時間単位の工数進捗値を直接使用する。
 */
const getWorkloadProgress = (
  params: ValueGetterParams<ProgramManagementRow>
): ProgramManagementProgress | null => {
  const progress = params.data?.progress
  if (!progress) return null

  const ctx = params.context as ProgramManagementGridContext | undefined
  const isTask = ctx?.aggregateTarget === WbsItemType.TASK
  return isTask ? progress.taskWorkload : progress.deliverableWorkload
}

/**
 * 進捗とリソースから EVM 生産性指標を算出する。
 *
 * EV = completed（完了工数: 時間）
 * PV = scheduledCompleted（予定完了工数: 時間）
 * AC = actualResource（投入工数: 時間）
 * BAC = total（総工数: 時間）
 */
export const calculateProductivity = (
  params: ValueGetterParams<ProgramManagementRow>,
  metric: ProductivityMetric
): number | null => {
  const data = params.data
  if (!data) return null

  const progress = getWorkloadProgress(params)
  const resource = data.resource

  if (!progress || !resource) return null

  const { total, scheduledCompleted, completed } = progress
  const { actualResource } = resource

  if (total === 0) return null

  const ev = completed
  const pv = scheduledCompleted
  const ac = actualResource
  const bac = total

  switch (metric) {
    case 'sv':
      return ev - pv
    case 'spi':
      return pv === 0 ? null : ev / pv
    case 'cv':
      return ev - ac
    case 'cpi':
      return ac === 0 ? null : ev / ac
    case 'etc': {
      const cpi = ac === 0 ? null : ev / ac
      if (!cpi || cpi === 0) return null
      return (bac - ev) / cpi
    }
    case 'eac': {
      const cpiForEac = ac === 0 ? null : ev / ac
      if (!cpiForEac || cpiForEac === 0) return null
      return bac / cpiForEac
    }
    default:
      return null
  }
}
